// ─── Coach forms and habits: pure helpers for the member app ───
// Custom check-in / intake forms and daily habits a coach assigns (API: `my_forms`,
// `submit_form`, `my_habits`, `log_habit` in ironlog-api/api/org/index.mjs). No imports
// so tests/coach-forms.test.mjs can load this file directly in Node.
//
// Every date here is a calendar date string ("2026-09-29") in the member's local
// time zone. Arithmetic runs in UTC on those strings so no time zone can shift a day.

export const FORMS_CACHE_KEY = "ft-org-forms";
export const HABITS_CACHE_KEY = "ft-org-habits";
export const FORM_NOTE_SEEN_KEY = "ft-org-form-note-seen";
export const FORM_DRAFT_PREFIX = "ft-form-draft:";
export const formDraftKey = (assignmentId) => FORM_DRAFT_PREFIX + assignmentId;

// Only clients answer forms and tick habits; owners and coaches never see these cards.
export const isCoachingClient = (me) => !!(me && me.org && me.role !== "owner" && me.role !== "coach");

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const utc = (date) => new Date(date + "T00:00:00Z");
export const addDays = (date, n) => { const d = utc(date); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const daysBetween = (from, to) => Math.round((utc(to) - utc(from)) / 86400000);
export const weekdayOf = (date) => utc(date).getUTCDay();
export const mondayOf = (date) => addDays(date, -((weekdayOf(date) + 6) % 7));
const monthDay = (date) => { const d = utc(date); return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`; };

// ─── Forms ───

// "Weekly check-in" / "Check-in" / "Intake"
export function formKindLabel(item) {
  const kind = item && item.form ? item.form.kind : item && item.kind;
  if (kind === "intake") return "Intake";
  return item && item.cadence === "weekly" ? "Weekly check-in" : "Check-in";
}

// Due-date wording for an assignment's `next_due` seen on `today`:
//   Due today · Due tomorrow · Due Sunday (within the week) · Due Oct 12
//   Overdue since yesterday · Overdue since Tue (within the week) · Overdue since Sep 3
// `tone` is 'today' | 'overdue' | 'upcoming' for colouring.
export function dueInfo(nextDue, today) {
  if (!DATE_RE.test(nextDue || "") || !DATE_RE.test(today || "")) return { text: "", tone: "upcoming" };
  const diff = daysBetween(today, nextDue);
  if (diff === 0) return { text: "Due today", tone: "today" };
  if (diff === 1) return { text: "Due tomorrow", tone: "upcoming" };
  if (diff > 1 && diff < 7) return { text: `Due ${WEEKDAYS[weekdayOf(nextDue)]}`, tone: "upcoming" };
  if (diff >= 7) return { text: `Due ${monthDay(nextDue)}`, tone: "upcoming" };
  if (diff === -1) return { text: "Overdue since yesterday", tone: "overdue" };
  if (diff > -7) return { text: `Overdue since ${WEEKDAYS_SHORT[weekdayOf(nextDue)]}`, tone: "overdue" };
  return { text: `Overdue since ${monthDay(nextDue)}`, tone: "overdue" };
}
export const dueLabel = (nextDue, today) => dueInfo(nextDue, today).text;

// The coach note to show: the most recently reviewed answer that carries a note.
export function latestReviewNote(recent) {
  let best = null;
  for (const r of Array.isArray(recent) ? recent : []) {
    if (!r || !r.reviewed_at || typeof r.review_note !== "string" || !r.review_note.trim()) continue;
    if (!best || r.reviewed_at > best.reviewed_at) best = r;
  }
  return best;
}

// What the "From your coach" forms card shows for a `my_forms` response on `today`. Due and
// upcoming are re-split on the member's date, so a response cached yesterday still reads
// right. `seenNote` is the response id whose note the member already dismissed. Hidden when
// nothing is assigned and there is no new note.
export function formsCardView(data, today, seenNote) {
  const rows = [...(Array.isArray(data && data.due) ? data.due : []), ...(Array.isArray(data && data.upcoming) ? data.upcoming : [])]
    .filter(r => r && r.assignment_id && r.form)
    .sort((a, b) => String(a.next_due).localeCompare(String(b.next_due)));
  const due = today ? rows.filter(r => r.next_due <= today) : rows;
  const upcoming = today ? rows.filter(r => r.next_due > today) : [];
  const n = latestReviewNote(data && data.recent);
  const note = n && n.id !== seenNote ? n : null;
  return { due, upcoming, next: due.length ? null : upcoming[0] || null, note, visible: !!(rows.length || note) };
}

const finite = (v) => typeof v === "number" && Number.isFinite(v);
const fail = (error, question) => ({ error, detail: { question } });

// Exact mirror of validateAnswers in ironlog-api/api/_lib/coach-forms.mjs: the cleaned answer
// map, or { error: 'answer_required' | 'invalid_answer' | 'invalid_answers', detail: { question } }
// for the first question that fails.
export function validateAnswers(questions, answers) {
  if (!answers || typeof answers !== "object" || Array.isArray(answers)) return { error: "invalid_answers", detail: {} };
  const out = {};
  for (const q of questions || []) {
    const v = answers[q.id];
    const missing = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
    const bad = () => fail("invalid_answer", q.id);
    if (missing) { if (q.required) return fail("answer_required", q.id); continue; }
    switch (q.type) {
      case "text": case "long_text": {
        const max = q.type === "text" ? 200 : 4000;
        if (typeof v !== "string") return bad();
        const t = v.trim();
        if (!t) { if (q.required) return fail("answer_required", q.id); continue; }
        if (t.length > max) return bad();
        out[q.id] = t; break;
      }
      case "number":
        if (!finite(v) || Math.abs(v) > 1e7) return bad();
        if (q.min != null && v < q.min) return bad();
        if (q.max != null && v > q.max) return bad();
        out[q.id] = v; break;
      case "scale":
        if (!Number.isInteger(v) || v < (q.min ?? 1) || v > (q.max ?? 10)) return bad();
        out[q.id] = v; break;
      case "yes_no":
        if (typeof v !== "boolean") return bad();
        out[q.id] = v; break;
      case "choice":
        if (typeof v !== "string" || !(q.options || []).includes(v)) return bad();
        out[q.id] = v; break;
      case "multi_choice":
        if (!Array.isArray(v) || v.some(x => typeof x !== "string" || !(q.options || []).includes(x))) return bad();
        out[q.id] = q.options.filter(o => v.includes(o)); break;
      default: return bad();
    }
  }
  return { answers: out };
}

// Every failing question at once ({ [id]: 'answer_required' | 'invalid_answer' }), judged by
// the same rule as the server, so the sheet can mark them all before sending.
export function answerErrors(questions, answers) {
  const errs = {};
  for (const q of questions || []) {
    const r = validateAnswers([q], answers || {});
    if (r.error) errs[q.id] = r.error;
  }
  return errs;
}

// The sheet keeps a number as the text the member typed ("72,5"); the API wants a number.
// Anything that does not parse stays a string so validation reports it as invalid.
export function coerceAnswers(questions, draft) {
  const out = {};
  for (const q of questions || []) {
    const v = draft ? draft[q.id] : undefined;
    if (v === undefined) continue;
    if (q.type === "number" && typeof v === "string") {
      const t = v.trim().replace(",", ".");
      out[q.id] = t === "" ? undefined : (/^[-+]?(\d+\.?\d*|\.\d+)$/.test(t) ? Number(t) : v);
      if (out[q.id] === undefined) delete out[q.id];
    } else out[q.id] = v;
  }
  return out;
}

const num = (n) => String(n);
// "40–200 kg", "at least 0", "up to 10 h", or "" when unbounded
export function numberRange(q) {
  const unit = q && q.unit ? ` ${q.unit}` : "";
  if (q && q.min != null && q.max != null) return `${num(q.min)}–${num(q.max)}${unit}`;
  if (q && q.min != null) return `at least ${num(q.min)}${unit}`;
  if (q && q.max != null) return `up to ${num(q.max)}${unit}`;
  return "";
}

// Plain-language message for a question that failed validation.
export function answerErrorText(q, code) {
  if (code === "answer_required") return "Answer this question to send the form.";
  switch (q && q.type) {
    case "number": { const r = numberRange(q); return r ? `Enter a number, ${r}.` : "Enter a number."; }
    case "scale": return `Pick a number from ${q.min ?? 1} to ${q.max ?? 10}.`;
    case "text": return "Keep this under 200 characters.";
    case "long_text": return "Keep this under 4000 characters.";
    case "yes_no": return "Choose Yes or No.";
    case "choice": return "Pick one of the options.";
    case "multi_choice": return "Pick from the options listed.";
    default: return "This answer isn't valid.";
  }
}

// Short read-only rendering of an answer (for the coach-note preview).
export function answerText(q, v) {
  if (v === undefined || v === null || v === "") return "—";
  if (q && q.type === "yes_no") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  if (q && q.type === "number" && q.unit) return `${v} ${q.unit}`;
  if (q && q.type === "scale") return `${v} / ${q.max ?? 10}`;
  return String(v);
}

// ─── Habits ───

export const HABIT_LOG_WINDOW_DAYS = 7;
// Today or one of the previous six days, never ahead (mirrors loggableDate on the API).
export const loggableDate = (date, today) => DATE_RE.test(date || "") && date <= today && date > addDays(today, -HABIT_LOG_WINDOW_DAYS);

// The Mon–Sun row for this week: { date, letter, name, done, today, future, loggable }.
export function weekDots(doneDates, weekStart, today) {
  const done = new Set(Array.isArray(doneDates) ? doneDates : []);
  const start = DATE_RE.test(weekStart || "") ? weekStart : mondayOf(today);
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(start, i);
    const dow = weekdayOf(date);
    return { date, letter: WEEKDAYS[dow][0], name: `${WEEKDAYS[dow]} ${monthDay(date)}`, done: done.has(date),
      today: date === today, future: date > today, loggable: loggableDate(date, today) };
  });
}

export function weekDoneCount(doneDates, weekStart, today) {
  const start = DATE_RE.test(weekStart || "") ? weekStart : mondayOf(today);
  return new Set((Array.isArray(doneDates) ? doneDates : []).filter(d => d >= start && d <= today)).size;
}

// Optimistic copy of a habit with `date` ticked or unticked.
export function toggleHabitDate(habit, date, done, today) {
  const set = new Set(Array.isArray(habit.done_dates) ? habit.done_dates : []);
  if (done) set.add(date); else set.delete(date);
  const done_dates = [...set].sort();
  const week_start = habit.week_start || mondayOf(today);
  return { ...habit, done_dates, week_start, week_done: weekDoneCount(done_dates, week_start, today) };
}
